import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

const scanLine = keyframes`
  0% { transform: translateY(-100%); }
  50% { transform: translateY(100%); }
  100% { transform: translateY(-100%); }
`;

const slideInFromTop = keyframes`
  from { opacity: 0; transform: translateY(-30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInFromBottom = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

const dataFlow = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 100% 100%; }
`;

const floatingParticles = keyframes`
  0% { transform: translateY(0) translateX(0) rotate(0); }
  25% { transform: translateY(-10px) translateX(5px) rotate(5deg); }
  50% { transform: translateY(0) translateX(10px) rotate(0); }
  75% { transform: translateY(10px) translateX(5px) rotate(-5deg); }
  100% { transform: translateY(0) translateX(0) rotate(0); }
`;

const revealFadeIn = keyframes`
  0% { opacity: 0; transform: translateY(24px) scale(0.96); filter: blur(6px); }
  60% { opacity: 0.7; filter: blur(2px); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  cursor: ${props => !props.showFullContent ? 'pointer' : 'default'};
  background: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      linear-gradient(45deg, rgba(0, 0, 0, 0.7), rgba(10, 20, 40, 0.7)),
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(100, 182, 255, 0.05) 2px,
        rgba(100, 182, 255, 0.05) 4px
      );
    z-index: -1;
    opacity: 0.8;
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 200%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(100, 182, 255, 0.05) 50%,
      transparent 100%
    );
    animation: ${scanLine} 8s linear infinite;
    z-index: -1;
    pointer-events: none;
  }
`;

const ContentPreview = styled.div`
  max-width: 80%;
  width: 80%;
  margin: 0 auto;
  padding: 30px 40px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.2);
  backdrop-filter: blur(10px);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(to bottom,
      rgba(100, 182, 255, 0),
      rgba(100, 182, 255, 0.2),
      rgba(100, 182, 255, 0));
    z-index: 10;
    animation: ${scanLine} 6s linear infinite;
    animation-delay: 2s;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.8), transparent);
  }

  opacity: 0;
  animation: ${scaleIn} 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.5s;
`;

const InitialPriceView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
  border-radius: 15px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(100, 182, 255, 0.2);
  box-shadow: 0 0 30px rgba(100, 182, 255, 0.1);
  max-width: 80%;
  width: 80%;

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: -1;
    border-radius: 15px;
    background:
      repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 20px,
        rgba(100, 182, 255, 0.03) 20px,
        rgba(100, 182, 255, 0.03) 40px
      );
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: -1;
    border-radius: 15px;
    background: linear-gradient(
      135deg,
      rgba(100, 182, 255, 0.1) 0%,
      transparent 100%
    );
    filter: blur(10px);
  }
`;

const InitialPriceHighlight = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 5.5rem;
  font-weight: 700;
  color: transparent;
  background: linear-gradient(45deg, #64B6FF, #00FFCC, #00FFFF);
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  text-shadow: 0 0 32px rgba(100, 182, 255, 0.7);
  margin-bottom: 30px;
  position: relative;
  animation:
    ${slideInFromTop} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
    ${dataFlow} 8s linear infinite;

  span {
    font-size: 2rem;
    margin-left: 5px;
    opacity: 0.9;
    display: block;
    text-align: center;
    color: #64B6FF;
  }
`;

const InitialSubtext = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  color: #64B6FF;
  text-align: center;
  opacity: 0;
  animation: ${slideInFromBottom} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.5s;
  letter-spacing: 1px;

  span {
    color: #00FFCC;
    font-weight: 600;
  }
`;

const TitleHighlight = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.8rem;
  font-weight: 700;
  color: #64B6FF;
  margin-bottom: 24px;
  text-align: center;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  animation: ${slideInFromTop} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.2s;
`;

const MonthlyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const MonthlyPrice = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.4rem;
  font-weight: 700;
  color: #00FFCC;
  text-shadow: 0 0 12px rgba(0, 255, 204, 0.6);
  span {
    font-size: 1.4rem;
    font-weight: 400;
    margin-left: 8px;
    color: #64B6FF;
  }
`;

const MonthlyFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeatureItem = styled.li`
  font-family: 'Orbitron', sans-serif;
  color: #64B6FF;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
  svg {
    color: #00FFCC;
    font-size: 0.9rem;
  }
`;

const ParticleContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.size || '4px'};
  height: ${props => props.size || '4px'};
  background: ${props => props.color || 'rgba(100, 182, 255, 0.5)'};
  border-radius: 50%;
  top: ${props => props.top};
  left: ${props => props.left};
  opacity: ${props => props.opacity || 0.5};
  animation: ${floatingParticles} ${props => props.duration || '8s'} infinite;
  animation-delay: ${props => props.delay || '0s'};
  box-shadow: 0 0 5px ${props => props.color || 'rgba(100, 182, 255, 0.5)'};
`;

const AnimatedReveal = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  animation: ${revealFadeIn} 0.7s cubic-bezier(0.23, 1, 0.32, 1);
`;

const particles = [
  { size: '5px', color: 'rgba(100, 182, 255, 0.7)', top: '15%', left: '10%', duration: '9s', delay: '0s', opacity: 0.7 },
  { size: '3px', color: 'rgba(0, 255, 204, 0.6)', top: '25%', left: '20%', duration: '10s', delay: '0.5s', opacity: 0.6 },
  { size: '6px', color: 'rgba(100, 182, 255, 0.5)', top: '60%', left: '85%', duration: '12s', delay: '1s', opacity: 0.5 },
  { size: '4px', color: 'rgba(0, 255, 204, 0.7)', top: '80%', left: '30%', duration: '11s', delay: '1.5s', opacity: 0.7 },
  { size: '7px', color: 'rgba(100, 182, 255, 0.6)', top: '40%', left: '70%', duration: '9s', delay: '2s', opacity: 0.6 },
  { size: '3px', color: 'rgba(0, 255, 255, 0.8)', top: '20%', left: '80%', duration: '8s', delay: '2.5s', opacity: 0.8 },
  { size: '5px', color: 'rgba(100, 182, 255, 0.7)', top: '70%', left: '10%', duration: '10s', delay: '3s', opacity: 0.7 },
  { size: '4px', color: 'rgba(0, 255, 204, 0.6)', top: '35%', left: '40%', duration: '11s', delay: '3.5s', opacity: 0.6 },
];

const RivieraSlidePreco = () => {
  const [showFullContent, setShowFullContent] = useState(false);

  const handleSlideClick = () => {
    if (!showFullContent) {
      setShowFullContent(true);
    }
  };

  return (
    <SlideWrapper onClick={handleSlideClick} showFullContent={showFullContent}>
      <ParticleContainer>
        {particles.map((p, i) => (
          <Particle key={i} {...p} />
        ))}
      </ParticleContainer>
      {!showFullContent ? (
        <InitialPriceView>
          <InitialPriceHighlight>
            R$ 4.000<span>por mês de mensalidade</span>
          </InitialPriceHighlight>
          <InitialSubtext>
            + custos de <span>tráfego</span> e <span>nutrição</span>
          </InitialSubtext>
        </InitialPriceView>
      ) : (
        <AnimatedReveal>
          <ContentPreview>
            <MonthlyContainer>
              <TitleHighlight>Mensalidade</TitleHighlight>
              <MonthlyPrice>
                R$ 4.000,00 <span>/ mês</span>
              </MonthlyPrice>
              <MonthlyFeatures>
                <FeatureItem><FontAwesomeIcon icon={faCheck} /> Geração completa de visitas</FeatureItem>
                <FeatureItem><FontAwesomeIcon icon={faCheck} /> 1 anúncio gerado por IA por mês</FeatureItem>
                <FeatureItem><FontAwesomeIcon icon={faCheck} /> Gestão de tráfego</FeatureItem>
                <FeatureItem><FontAwesomeIcon icon={faCheck} /> Agendamento dos leads com a Bianca</FeatureItem>
              </MonthlyFeatures>
            </MonthlyContainer>
          </ContentPreview>
        </AnimatedReveal>
      )}
    </SlideWrapper>
  );
};

export default RivieraSlidePreco;
